import { parseSSE, type SSEEvent } from './sse';
import type {
  ChatMessage,
  CookieRow,
  Effort,
  ModelAlias,
  Preset,
} from './types';

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

export async function login(user: string, password: string): Promise<void> {
  await jsonFetch('/login', {
    method: 'POST',
    body: JSON.stringify({ user, password }),
  });
}

export async function logout(): Promise<void> {
  await jsonFetch('/logout', { method: 'POST' });
}

export async function checkAuth(): Promise<boolean> {
  const res = await fetch('/api/health', { credentials: 'include' });
  return res.ok;
}

export async function listPresets(): Promise<Preset[]> {
  const data = await jsonFetch<{ presets: Preset[] }>('/api/presets');
  return data.presets;
}

export interface ChatRequest {
  messages: ChatMessage[];
  model?: ModelAlias | string;
  effort?: Effort;
  preset_ids?: string[];
  system_prompt?: string;
  tools?: string[];
  native_tools?: ('code_execution')[];
  health_context?: boolean;
}

export async function* streamChat(req: ChatRequest): AsyncGenerator<SSEEvent> {
  const res = await fetch('/api/chat', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`chat ${res.status}: ${text.slice(0, 200)}`);
  }
  if (!res.body) throw new Error('no response body');
  yield* parseSSE(res.body);
}

export interface SwarmRequest {
  messages: ChatMessage[];
  model?: ModelAlias | string;
  effort?: Effort;
  preset_ids?: string[];
  system_prompt?: string;
  judge?: boolean;
  mode?: 'perspectives' | 'debate';
}

export async function* streamSwarm(
  req: SwarmRequest,
): AsyncGenerator<SSEEvent> {
  const res = await fetch('/api/swarm', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`swarm ${res.status}: ${text.slice(0, 200)}`);
  }
  if (!res.body) throw new Error('no response body');
  yield* parseSSE(res.body);
}

export async function listCookies(domain?: string): Promise<CookieRow[]> {
  const url = domain
    ? `/api/admin/cookies?domain=${encodeURIComponent(domain)}`
    : '/api/admin/cookies';
  const data = await jsonFetch<{ cookies: CookieRow[] }>(url);
  return data.cookies;
}

export interface UpsertCookieInput {
  domain: string;
  name: string;
  value: string;
  label?: string;
}

export async function upsertCookie(
  input: UpsertCookieInput,
): Promise<CookieRow> {
  return jsonFetch<CookieRow>('/api/admin/cookies', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function deleteCookie(id: number): Promise<void> {
  await jsonFetch<{ ok: boolean }>(`/api/admin/cookies/${id}`, {
    method: 'DELETE',
  });
}
